import { RefObject, useEffect, useRef } from "react";

/**
 * 영역(보통 SnGrid 를 감싼 div) 이 떠 있는 동안 Ctrl+V 한 클립보드 텍스트를 화면 핸들러로 넘긴다.
 * 커서가 어느 행·칸에 있든, 행이 하나도 없든, 아직 그리드를 한 번도 클릭하지 않았든 같은 결과를 낸다.
 * 셀 편집 중이거나 입력칸(input/textarea)에 포커스가 있으면 막지 않아 그 칸에 그냥 붙여넣어진다.
 * SnGrid 를 건드리지 않고 화면에서 골라 쓰는 방식. 해석은 GridPasteUtils.parseGridPaste.
 *
 * 한 화면에 이 훅을 두 번 쓰지 말 것(document 리스너라 양쪽이 다 받는다).
 * 같은 SnGrid 에 enablePasteOnlyAdd 를 같이 켜지 말 것(두 번 들어감).
 */
export const useGridPaste = (
  areaRef: RefObject<HTMLElement>,
  onPaste: (text: string) => void
) => {
  // 핸들러가 매 렌더마다 바뀌어도 리스너는 한 번만 단다.
  const handlerRef = useRef(onPaste);
  handlerRef.current = onPaste;

  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    // SnGrid(GridV2) 가 선택된 글자 칸에 깔아 두는 숨은 입력칸은 편집 중이 아니면 입력칸으로 보지 않는다 (SnGridIme.tsx)
    const isEditing = (t: EventTarget | null) =>
      t instanceof HTMLElement &&
      !t.matches(".sn-ime-input:not(.sn-ime-input--editing)") &&
      !!t.closest("input, textarea, [contenteditable='true']");
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v" && !isEditing(e.target)) {
        e.stopPropagation(); // AG Grid 의 Ctrl+V 처리로 안 내려가게 — 기본 동작(paste 이벤트)은 그대로
      }
    };
    const onPasteEvent = (e: ClipboardEvent) => {
      // 영역이 화면에서 빠졌으면(팝업 닫힘 등) 처리하지 않는다.
      if (!areaRef.current?.isConnected) return;
      if (isEditing(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      handlerRef.current(e.clipboardData?.getData("text/plain") ?? "");
    };
    // keydown 은 영역에만 — AG Grid 기본 붙여넣기를 막는 용도라 포커스가 그리드 안에 있을 때만 의미가 있다.
    el.addEventListener("keydown", onKeyDown, true);
    // paste 는 document 에 — paste 이벤트는 "포커스된 엘리먼트"에서 나므로,
    // 팝업(MUI Dialog)이 열린 직후처럼 포커스가 영역 밖에 있으면 영역 리스너까지 내려오지 않는다.
    // Firefox 는 포커스 불가 엘리먼트를 클릭해도 tabIndex=-1 조상으로 포커스를 올려주지 않아 더 자주 걸린다.
    document.addEventListener("paste", onPasteEvent, true);
    return () => {
      el.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("paste", onPasteEvent, true);
    };
  }, [areaRef]);
};
